import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Mic, Trophy, Brain, Music, ArrowRight,
  Play, BarChart2, Headphones, Sparkles, ChevronRight
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { AppStoreBadges } from '../components/AppStoreBadges';

const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
};

const AnimatedCounter: React.FC<{ end: number; suffix?: string; duration?: number }> = ({
  end,
  suffix = '',
  duration = 2000
}) => {
  const [count, setCount] = useState(0);
  const { ref, isVisible } = useInView(0.3);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref}>
      {count.toLocaleString()}{suffix}
    </span>
  );
};

export const LandingPage: React.FC = () => {
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    navigate(isAuthenticated ? '/dashboard' : '/signup');
  };

  const features = [
    {
      icon: <Brain className="w-5 h-5" />,
      label: 'AI-Powered',
      title: 'Vocal Blueprint Analysis',
      description: 'Receive instant, detailed feedback on pitch accuracy, rhythm, tone quality, and technique from our proprietary AI scoring engine.',
      image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=600&q=80',
      accent: 'violet',
      badgeBg: 'bg-violet-500/10 border-violet-500/20 text-violet-300',
      hoverBorder: 'hover:border-violet-500/30',
    },
    {
      icon: <Trophy className="w-5 h-5" />,
      label: 'Compete',
      title: 'Live Tournaments',
      description: 'Enter bracketed competitions with real prizes. Climb the leaderboard, earn your ranking, and prove you belong on the main stage.',
      image: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=600&q=80',
      accent: 'amber',
      badgeBg: 'bg-amber-500/10 border-amber-500/20 text-amber-300',
      hoverBorder: 'hover:border-amber-500/30',
    },
    {
      icon: <Headphones className="w-5 h-5" />,
      label: 'Studio Quality',
      title: 'Professional Backing Tracks',
      description: 'Sing along to studio-grade instrumentals from DigiTrax\'s licensed catalog. Adjust key and tempo to match your vocal range perfectly.',
      image: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600&q=80',
      accent: 'sky',
      badgeBg: 'bg-sky-500/10 border-sky-500/20 text-sky-300',
      hoverBorder: 'hover:border-sky-500/30',
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Choose Your Song',
      description: 'Browse our curated catalog of professional backing tracks across every genre.',
      icon: <Music className="w-5 h-5" />,
      iconColor: 'text-sky-400',
      ringColor: 'group-hover:border-sky-400/40 group-hover:shadow-[0_0_30px_rgba(56,189,248,0.12)]',
      iconBg: 'from-sky-500/20 to-sky-400/5',
    },
    {
      number: '02',
      title: 'Record Your Performance',
      description: 'Capture audio or video with our studio-quality recording tools. No equipment needed.',
      icon: <Mic className="w-5 h-5" />,
      iconColor: 'text-violet-400',
      ringColor: 'group-hover:border-violet-400/40 group-hover:shadow-[0_0_30px_rgba(139,92,246,0.12)]',
      iconBg: 'from-violet-500/20 to-violet-400/5',
    },
    {
      number: '03',
      title: 'Get Your Score',
      description: 'Our AI analyzes your pitch, timing, and technique to generate your Vocal Blueprint.',
      icon: <BarChart2 className="w-5 h-5" />,
      iconColor: 'text-emerald-400',
      ringColor: 'group-hover:border-emerald-400/40 group-hover:shadow-[0_0_30px_rgba(52,211,153,0.12)]',
      iconBg: 'from-emerald-500/20 to-emerald-400/5',
    },
    {
      number: '04',
      title: 'Compete & Climb',
      description: 'Submit your best performances to tournaments and climb the global leaderboard.',
      icon: <Trophy className="w-5 h-5" />,
      iconColor: 'text-amber-400',
      ringColor: 'group-hover:border-amber-400/40 group-hover:shadow-[0_0_30px_rgba(251,191,36,0.12)]',
      iconBg: 'from-amber-500/20 to-amber-400/5',
    }
  ];

  const section1 = useInView();
  const section2 = useInView();
  const section3 = useInView();
  const section5 = useInView();

  return (
    <div className="flex flex-col min-h-screen bg-purple-950 text-white overflow-x-hidden">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#1a0a2e]/90 backdrop-blur-xl border-b border-white/[0.1] py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/TSL_Logo.png"
              alt="The Singing League"
              className="h-20 w-20 object-contain"
            />
            <span className="font-bold text-lg tracking-tight hidden sm:block">The Singing League</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/help"
              className="hidden sm:inline-flex px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              Support
            </Link>
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="px-5 py-2.5 text-sm font-semibold bg-violet-600 hover:bg-violet-500 rounded-lg hover:shadow-lg hover:shadow-violet-500/20 transition-all duration-300"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2.5 text-sm font-medium text-white/80 hover:text-white transition-colors"
                >
                  Log In
                </Link>
                <button
                  onClick={handleGetStarted}
                  className="px-5 py-2.5 text-sm font-semibold bg-violet-600 hover:bg-violet-500 rounded-lg hover:shadow-lg hover:shadow-violet-500/20 transition-all duration-300"
                >
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="landing-hero-glow" />
        <div className="landing-hero-grid" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="landing-fade-up" style={{ animationDelay: '0.1s' }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.06] border border-white/[0.1] text-sm text-white/70 mb-8 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>AI-Powered Vocal Analysis</span>
              <ChevronRight className="w-3.5 h-3.5 text-white/40" />
            </div>
          </div>

          <h1 className="landing-fade-up text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] font-bold tracking-tight leading-[1.08] mb-7" style={{ animationDelay: '0.2s' }}>
            <span className="block">Where singers</span>
            <span className="block landing-text-gradient">become champions</span>
          </h1>

          <p className="landing-fade-up text-lg sm:text-xl text-white/50 max-w-2xl mx-auto mb-10 leading-relaxed" style={{ animationDelay: '0.35s' }}>
            Record, get scored by AI, and compete in live tournaments with
            real prizes. The professional singing platform built for
            performers who take their craft seriously.
          </p>

          <div className="landing-fade-up flex flex-col sm:flex-row justify-center gap-4 mb-8" style={{ animationDelay: '0.5s' }}>
            <button
              onClick={handleGetStarted}
              className="group px-8 py-4 bg-violet-600 hover:bg-violet-500 rounded-xl text-base font-semibold hover:shadow-2xl hover:shadow-violet-500/25 transition-all duration-300 flex items-center justify-center gap-2"
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Start Singing Free'}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => {
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-8 py-4 rounded-xl text-base font-semibold border border-white/[0.15] bg-white/[0.04] hover:bg-white/[0.08] transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Play className="w-4 h-4" />
              See How It Works
            </button>
          </div>

          <div className="landing-fade-up flex flex-col items-center gap-3 mb-16" style={{ animationDelay: '0.6s' }}>
            <p className="text-xs text-white/35 uppercase tracking-widest font-medium">Also available on mobile</p>
            <AppStoreBadges />
          </div>

          <div className="landing-fade-up" style={{ animationDelay: '0.65s' }}>
            <div className="relative mx-auto max-w-4xl rounded-2xl overflow-hidden border border-white/[0.1] shadow-2xl shadow-black/40">
              <img
                src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200&q=80"
                alt="Vocalist performing on stage"
                className="w-full h-auto object-cover aspect-[16/9]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-950 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center">
                    <Mic className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold">Live Recording Session</p>
                    <p className="text-xs text-white/50">AI scoring in real-time</p>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm text-sm">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-white/80">92.4 Score</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-purple-950 to-transparent" />
      </section>

      {/* Stats Bar */}
      <section
        ref={section1.ref}
        className={`relative z-10 py-16 landing-section-reveal ${section1.isVisible ? 'is-visible' : ''}`}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-violet-900/20 via-purple-900/40 to-violet-900/20 backdrop-blur-sm" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { value: 10000, suffix: '+', label: 'Active Singers', color: 'text-violet-300' },
              { value: 500, suffix: '+', label: 'Professional Tracks', color: 'text-sky-300' },
              { value: 2500, suffix: '+', label: 'Tournaments Held', color: 'text-amber-300' },
              { value: 98, suffix: '%', label: 'User Satisfaction', color: 'text-emerald-300' }
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className={`text-3xl md:text-4xl font-bold tracking-tight mb-1 ${stat.color}`}>
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-sm text-white/50">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section
        ref={section2.ref}
        className={`relative py-24 md:py-32 overflow-hidden landing-section-reveal ${section2.isVisible ? 'is-visible' : ''}`}
      >
        <div className="landing-section-glow-features" />
        <div className="landing-section-noise" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <p className="text-sm font-semibold text-violet-400 tracking-wide uppercase mb-3">Platform Features</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
              Everything you need to perform at your peak
            </h2>
            <p className="text-lg text-white/40 leading-relaxed">
              Professional-grade tools that used to require a recording studio, a vocal coach, and a competition circuit -- all in one place.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={feature.title}
                className={`group relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm overflow-hidden ${feature.hoverBorder} transition-all duration-500`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-950 via-purple-950/60 to-transparent" />
                </div>
                <div className="relative p-6 -mt-16 z-10">
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium mb-4 ${feature.badgeBg}`}>
                    {feature.icon}
                    {feature.label}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section
        id="how-it-works"
        ref={section3.ref}
        className={`py-24 md:py-36 relative overflow-hidden landing-section-reveal ${section3.isVisible ? 'is-visible' : ''}`}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-[#1a0a2e] to-purple-900/15 pointer-events-none" />
        <div className="landing-section-glow-steps" />
        <div className="landing-section-noise" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-violet-400/[0.08] to-transparent" />

        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="text-center mb-20 md:mb-24">
            <p className="text-sm font-semibold text-sky-400 tracking-wide uppercase mb-3">How It Works</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-5">
              From warmup to winning in four steps
            </h2>
            <p className="text-lg text-white/45 max-w-xl mx-auto">
              No complicated setup, no expensive equipment. Just your voice and a device.
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-[3.75rem] left-[12.5%] right-[12.5%] h-px">
              <div className="w-full h-full bg-gradient-to-r from-sky-500/15 via-violet-500/20 to-amber-500/15" />
            </div>

            <div className="grid md:grid-cols-4 gap-6 md:gap-5">
              {steps.map((step) => (
                <div key={step.number} className="group relative">
                  <div className="relative flex flex-col items-center text-center">
                    <div className="relative mb-8">
                      <div className="landing-step-number select-none">
                        {step.number}
                      </div>
                      <div className={`relative z-10 w-[4.5rem] h-[4.5rem] rounded-full border border-white/[0.1] bg-purple-950/80 backdrop-blur-sm flex items-center justify-center transition-all duration-500 ${step.ringColor}`}>
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${step.iconBg} flex items-center justify-center`}>
                          <div className={`${step.iconColor} transition-transform duration-300 group-hover:scale-110`}>
                            {step.icon}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm p-6 pt-5 w-full transition-all duration-500 group-hover:border-white/[0.15] group-hover:bg-white/[0.06]">
                      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-b from-white/[0.04] to-transparent pointer-events-none" />
                      <h3 className="text-[17px] font-bold mb-2 relative z-10">{step.title}</h3>
                      <p className="text-[13px] text-white/45 leading-relaxed relative z-10">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        ref={section5.ref}
        className={`relative py-24 md:py-32 overflow-hidden landing-section-reveal ${section5.isVisible ? 'is-visible' : ''}`}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/20 to-transparent pointer-events-none" />
        <div className="landing-section-noise" />
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden">
            <div className="landing-cta-glow" />
            <div className="relative z-10 border border-white/[0.12] rounded-3xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-md px-8 py-16 md:px-16 md:py-20 text-center">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-violet-500/[0.05] via-transparent to-sky-500/[0.05] pointer-events-none" />
              <h2 className="relative text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-5">
                Your stage is waiting
              </h2>
              <p className="relative text-lg text-white/50 max-w-lg mx-auto mb-10 leading-relaxed">
                Join thousands of singers who are sharpening their voice, winning tournaments, and building their reputation on The Singing League.
              </p>
              <div className="relative flex flex-col sm:flex-row justify-center gap-4">
                <button
                  onClick={handleGetStarted}
                  className="group px-8 py-4 bg-violet-600 hover:bg-violet-500 rounded-xl text-base font-semibold hover:shadow-2xl hover:shadow-violet-500/25 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {isAuthenticated ? 'Go to Dashboard' : 'Create Your Account'}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
              <div className="relative mt-8 flex flex-col items-center gap-2.5">
                <div className="flex items-center gap-3">
                  <div className="h-px w-8 bg-white/10" />
                  <p className="text-xs text-white/30 uppercase tracking-widest font-medium">Get the app</p>
                  <div className="h-px w-8 bg-white/10" />
                </div>
                <AppStoreBadges />
              </div>
              <p className="relative text-xs text-white/30 mt-6">Free to join. No credit card required.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/[0.06] bg-gradient-to-b from-purple-950 to-[#1a0530] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between gap-12 mb-12">
            <div className="max-w-xs">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img
                  src="/TSL_Logo.png"
                  alt="The Singing League"
                  className="h-20 w-20 object-contain"
                />
                <span className="font-bold text-lg tracking-tight">The Singing League</span>
              </Link>
              <p className="text-sm text-white/30 leading-relaxed mb-5">
                The professional singing platform combining AI analysis, live tournaments, and a global community of performers.
              </p>
              <AppStoreBadges size="sm" className="justify-start" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-10">
              <div>
                <h4 className="text-sm font-semibold mb-4 text-white/60">Platform</h4>
                <ul className="space-y-2.5">
                  <li><Link to="/songs" className="text-sm text-white/30 hover:text-white/70 transition-colors">Song Catalog</Link></li>
                  <li><Link to="/tournaments" className="text-sm text-white/30 hover:text-white/70 transition-colors">Tournaments</Link></li>
                  <li><Link to="/leaderboard" className="text-sm text-white/30 hover:text-white/70 transition-colors">Leaderboard</Link></li>
                  <li><Link to="/academy" className="text-sm text-white/30 hover:text-white/70 transition-colors">Academy</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4 text-white/60">Resources</h4>
                <ul className="space-y-2.5">
                  <li><Link to="/help" className="text-sm text-white/30 hover:text-white/70 transition-colors">Help & Support</Link></li>
                  <li><Link to="/privacy" className="text-sm text-white/30 hover:text-white/70 transition-colors">Privacy Policy</Link></li>
                  <li><Link to="/data-deletion" className="text-sm text-white/30 hover:text-white/70 transition-colors">Data Deletion</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4 text-white/60">Company</h4>
                <ul className="space-y-2.5">
                  <li><span className="text-sm text-white/30">DigiTrax Entertainment</span></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-white/[0.06] pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/20">
              &copy; {new Date().getFullYear()} DigiTrax Entertainment. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-xs text-white/20 hover:text-white/50 transition-colors">
                Privacy
              </Link>
              <Link to="/data-deletion" className="text-xs text-white/20 hover:text-white/50 transition-colors">
                Data Deletion
              </Link>
              <Link to="/help" className="text-xs text-white/20 hover:text-white/50 transition-colors">
                Support
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
